import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';
import { Card } from 'reactstrap';

import {
  activeMetaBroadcastGet,
  metaBroadcastDelete,
  metaBroadcastDownload,
  metaBroadcastStatusUpdate,
  updateMetaBroadcastActiveList,
  useMetaBroadcastActiveList
} from '../../../store/slices/tenantSlices/configurationSlice';

import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { checkModulePermission } from '../../../utils/common';
import { useDebouncedCallback } from 'use-debounce';

const MetaBroadcastActiveList = ({
  activeTab,
  isApiCall,
  setEditRecord,
  setOpenAddModal,
  setIsEdit,
  search = undefined
}) => {
  const dispatch = useDispatch();

  const navigation = useNavigate();

  const metaBroadcastActiveList = useMetaBroadcastActiveList();
  const userRolePermission = useUserRolePermission();

  const [filter, setFilter] = useState({ page: 1, limit: 10 });
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteId, setIsDeleteId] = useState(null);

  const fetchActiveList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter, search: search };
      await dispatch(activeMetaBroadcastGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteList = async () => {
    try {
      const response = await dispatch(metaBroadcastDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        fetchActiveList();
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchActiveList();
  }, [filter, activeTab, isApiCall, search]);

  const updateBroadcastStatus = async (value, id) => {
    try {
      const payload = {
        id: id,
        whatsapp_meta_broadcast_status: value?.whatsapp_meta_broadcast_status === 'on' ? 'off' : 'on'
      };
      const response = await dispatch(metaBroadcastStatusUpdate(payload)).unwrap();
      if (response) {
        toast.success('Status updated successfully');
        const updatedLists = metaBroadcastActiveList?.data.filter(
          (item) => item.whatsapp_meta_broadcast_id !== id
        );
        dispatch(
          updateMetaBroadcastActiveList({
            ...metaBroadcastActiveList,
            total: metaBroadcastActiveList?.total - 1,
            data: updatedLists
          })
        );
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const onDownloadClick = async (status, id) => {
    try {
      const payload = {
        status: status,
        whatsapp_meta_broadcast_id: id
      };
      const response = await dispatch(metaBroadcastDownload(payload)).unwrap();
      if (response) {
        const fileName = `${status}.xlsx`;
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <React.Fragment>
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 208px)' }}>
          <CallexLoader />
        </Card>
      ) : metaBroadcastActiveList?.data?.length > 0 &&
        checkModulePermission('is_view', 'Lists', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="lists-table" style={{ height: 'calc(100vh - 248px)', overflow: 'auto' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th style={{ width: '250px' }}>Name</th>
                  <th className="text-center" style={{ width: '150px' }}>
                    Pending Count
                  </th>
                  <th className="text-center" style={{ width: '150px' }}>
                    Sent Count
                  </th>
                  <th className="text-center" style={{ width: '150px' }}>
                    Success Count
                  </th>
                  <th className="text-center" style={{ width: '150px' }}>
                    Fail Count
                  </th>
                  <th className="text-center" style={{ width: '150px' }}>
                    Total count
                  </th>
                  <th className="text-center" style={{ width: '150px' }}>
                    Status
                  </th>
                  <th className="text-center" style={{ width: '120px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {metaBroadcastActiveList.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.whatsapp_meta_broadcast_name}</td>
                      <td
                        className="text-decoration-underline cursor-pointer text-center"
                        onClick={() => onDownloadClick('pending', val?.whatsapp_meta_broadcast_id)}>
                        {val?.pending_count}
                      </td>
                      <td
                        className="text-decoration-underline cursor-pointer text-center"
                        onClick={() => onDownloadClick('sent', val?.whatsapp_meta_broadcast_id)}>
                        {val?.sent_count}
                      </td>
                      <td
                        className="text-decoration-underline cursor-pointer text-center"
                        onClick={() => onDownloadClick('success', val?.whatsapp_meta_broadcast_id)}>
                        {val?.success_count}
                      </td>
                      <td
                        className="text-decoration-underline cursor-pointer text-center"
                        onClick={() => onDownloadClick('fail', val?.whatsapp_meta_broadcast_id)}>
                        {val?.fail_count}
                      </td>
                      <td
                        className="text-decoration-underline cursor-pointer text-center"
                        onClick={() => onDownloadClick('total', val?.whatsapp_meta_broadcast_id)}>
                        {val?.total_count}
                      </td>
                      <td
                        onClick={() => updateBroadcastStatus(val, val?.whatsapp_meta_broadcast_id)}
                        style={{
                          cursor: 'pointer',
                          background:
                            val?.whatsapp_meta_broadcast_status === 'on' ? '#2cabe333' : '#ff505033'
                        }}>
                        {val?.whatsapp_meta_broadcast_status === 'on' ? (
                          <div className="d-flex align-items-center gap-2 justify-content-center text-info">
                            <Icon.PlayCircle size={16} />
                            Active
                          </div>
                        ) : (
                          <div className="d-flex align-items-center gap-2 justify-content-center text-danger">
                            <Icon.StopCircle size={16} />
                            Inactive
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div className="cursor-pointer text-primary">
                            <Icon.Upload
                              size={16}
                              onClick={() =>
                                navigation(
                                  `/configuration/whatsapp-meta/broadcast/${val?.whatsapp_meta_broadcast_id}`
                                )
                              }
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', 'Lists', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (checkModulePermission('is_edit', 'Lists', userRolePermission)) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission('is_delete', 'Lists', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_delete', 'Lists', userRolePermission)
                                ) {
                                  setIsDeleteId(val?.whatsapp_meta_broadcast_id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={metaBroadcastActiveList?.total}
              limit={metaBroadcastActiveList?.limit}
              page={metaBroadcastActiveList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchActiveList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 208px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteList()}
      />
    </React.Fragment>
  );
};

MetaBroadcastActiveList.propTypes = {
  search: PropTypes.any,
  activeTab: PropTypes.string,
  isApiCall: PropTypes.bool,
  setEditRecord: PropTypes.func,
  setOpenAddModal: PropTypes.func,
  setIsEdit: PropTypes.func
};

export default MetaBroadcastActiveList;
